import React, { useEffect, useState } from "react";
import {
  Box,
  Grid,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Divider,
  Stack,
  Alert,
  LinearProgress
} from "@mui/material";
import { Close, Refresh, Visibility, CheckCircleOutline, ErrorOutline } from "@mui/icons-material";
import CaseApi from "Service/Case.api";

import { useNavigate } from "react-router-dom";

interface CorporateBatchDashboardProps {
  clientId: number;
}

const CorporateBatchDashboard: React.FC<CorporateBatchDashboardProps> = ({ clientId }) => {
  const navigate = useNavigate();
  const [batches, setBatches] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [selectedBatchDetails, setSelectedBatchDetails] = useState<any | null>(null);
  const [detailsOpen, setDetailsOpen] = useState<boolean>(false);
  const [loadingDetails, setLoadingDetails] = useState<boolean>(false);

  // Refs to avoid stale closures in polling interval
  const batchesRef = React.useRef<any[]>(batches);
  const selectedBatchDetailsRef = React.useRef<any | null>(selectedBatchDetails);
  const detailsOpenRef = React.useRef<boolean>(detailsOpen);
  React.useEffect(() => { batchesRef.current = batches; }, [batches]);
  React.useEffect(() => { selectedBatchDetailsRef.current = selectedBatchDetails; }, [selectedBatchDetails]);
  React.useEffect(() => { detailsOpenRef.current = detailsOpen; }, [detailsOpen]);

  const fetchBatches = React.useCallback(async (showLoading = false) => {
    if (!clientId) return;
    if (showLoading) setLoading(true);
    try {
      const res = await CaseApi.getClientBatches({ clientId });
      if (res?.success) {
        setBatches(res.data || []);
      }
    } catch (error) {
      console.error("Error fetching client batches:", error);
    } finally {
      if (showLoading) setLoading(false);
    }
  }, [clientId]);

  // Initial fetch when clientId changes
  useEffect(() => {
    fetchBatches(true);
  }, [clientId, fetchBatches]);

  const fetchBatchDetails = React.useCallback(async (batchId: number, silent = false) => {
    if (!silent) setLoadingDetails(true);
    try {
      const res = await CaseApi.getBatchDetails({ batchId });
      if (res?.success) {
        setSelectedBatchDetails(res.data);
      }
    } catch (error) {
      console.error("Error fetching batch details:", error);
    } finally {
      if (!silent) setLoadingDetails(false);
    }
  }, []);

  // Single stable polling interval — uses refs to read latest state without re-creating the interval
  useEffect(() => {
    const POLL_INTERVAL = 8000; // 8 seconds — no need for 5s hammering
    const interval = setInterval(() => {
      const currentBatches = batchesRef.current;
      const currentDetails = selectedBatchDetailsRef.current;
      const isOpen = detailsOpenRef.current;

      const hasActiveBatches = currentBatches.some(
        b => b.status === "Processing" || b.status === "PendingValidation"
      );
      const isDetailsActive = isOpen && currentDetails &&
        (currentDetails.status === "Processing" || currentDetails.status === "PendingValidation");

      if (hasActiveBatches) {
        fetchBatches(false);
      }
      if (isDetailsActive) {
        fetchBatchDetails(currentDetails.pkBatchId, true);
      }
    }, POLL_INTERVAL);

    return () => clearInterval(interval);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchBatches, fetchBatchDetails]); // stable deps only — interval created ONCE per mount

  const handleViewDetails = (batchId: number) => {
    setDetailsOpen(true);
    fetchBatchDetails(batchId, false);
  };

  const getStatusChip = (status: string) => {
    switch (status) {
      case "Completed":
        return <Chip label="Completed" color="success" size="small" variant="filled" sx={{ fontWeight: "bold" }} />;
      case "Processing":
        return <Chip label="Processing" color="info" size="small" variant="filled" icon={<CircularProgress size={14} sx={{ color: "inherit" }} />} sx={{ fontWeight: "bold" }} />;
      case "Failed":
        return <Chip label="Failed" color="error" size="small" variant="filled" sx={{ fontWeight: "bold" }} />;
      case "PendingValidation":
        return <Chip label="Validation Pending" color="warning" size="small" variant="filled" sx={{ fontWeight: "bold" }} />;
      default:
        return <Chip label={status} size="small" sx={{ fontWeight: "bold" }} />;
    }
  };

  return (
    <Box sx={{ mt: 4, px: 2 }}>
      <Paper elevation={0} sx={{ p: 4, borderRadius: "24px", border: "1px solid rgba(226, 232, 240, 0.8)", bgcolor: "white" }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
          <Box>
            <Typography variant="h6" fontWeight="800" color="primary.main" fontFamily="'Outfit', sans-serif">
              Corporate Bulk Filing History
            </Typography>
            <Typography variant="body2" color="text.primary">
              Monitor, audit, and inspect bulk ODR filings and automatic legal notices.
            </Typography>
          </Box>
          <IconButton onClick={() => fetchBatches(true)} color="primary" sx={{ border: "1px solid #e2e8f0", borderRadius: 2 }}>
            <Refresh fontSize="small" />
          </IconButton>
        </Stack>

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress color="primary" />
          </Box>
        ) : batches.length === 0 ? (
          <Box sx={{ textAlign: "center", py: 8, bgcolor: "#f8fafc", borderRadius: 3 }}>
            <Typography variant="body1" color="text.primary" mb={2}>
              No bulk upload history found.
            </Typography>
            <Typography variant="caption" color="text.primary">
              Your uploaded batch spreadsheets will appear here once submitted.
            </Typography>
          </Box>
        ) : (
          <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: "16px", border: "1px solid #edf2f7" }}>
            <Table>
              <TableHead sx={{ bgcolor: "#f8fafc" }}>
                <TableRow>
                  <TableCell><strong>Batch Ref / Key</strong></TableCell>
                  <TableCell><strong>Date Uploaded</strong></TableCell>
                  <TableCell><strong>CSV Filename</strong></TableCell>
                  <TableCell align="center"><strong>Progress</strong></TableCell>
                  <TableCell><strong>Current Status</strong></TableCell>
                  <TableCell align="right"><strong>Actions</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {batches.map((batch) => (
                  <TableRow key={batch.pkBatchId} hover>
                    <TableCell sx={{ fontFamily: "monospace", fontSize: "0.85rem" }}>{batch.batchNumber}</TableCell>
                    <TableCell>{new Date(batch.dateCreated).toLocaleString()}</TableCell>
                    <TableCell>{batch.csvFileName?.substring(batch.csvFileName?.indexOf("-") + 1) || batch.csvFileName}</TableCell>
                    <TableCell align="center" sx={{ fontWeight: "bold" }}>{batch.processedCases ?? 0} / {batch.totalCases}</TableCell>
                    <TableCell>{getStatusChip(batch.status)}</TableCell>
                    <TableCell align="right">
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<Visibility fontSize="small" />}
                        onClick={() => handleViewDetails(batch.pkBatchId)}
                        sx={{ textTransform: "none", borderRadius: 2 }}
                      >
                        Inspect
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Details Dialog */}
      <Dialog open={detailsOpen} onClose={() => setDetailsOpen(false)} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 4 } }}>
        <DialogTitle sx={{ m: 0, p: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Typography variant="h6" fontWeight="bold" color="primary.main">
            Batch Audit Details
          </Typography>
          <IconButton onClick={() => setDetailsOpen(false)} size="small">
            <Close />
          </IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ p: 3 }}>
          {loadingDetails ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
              <CircularProgress />
            </Box>
          ) : !selectedBatchDetails ? (
            <Alert severity="error">Unable to load details.</Alert>
          ) : (
            <Box>
              {/* Batch Metadata Header */}
              <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="caption" color="text.primary" display="block">BATCH REFERENCE</Typography>
                  <Typography variant="body1" fontWeight="bold" sx={{ fontFamily: "monospace" }}>{selectedBatchDetails.batchNumber}</Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="caption" color="text.primary" display="block">DATE CREATED</Typography>
                  <Typography variant="body1" fontWeight="bold">{new Date(selectedBatchDetails.dateCreated).toLocaleString()}</Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="caption" color="text.primary" display="block">REGISTRY CSV</Typography>
                  <Typography variant="body2" fontWeight="bold">{selectedBatchDetails.csvFileName}</Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="caption" color="text.primary" display="block">STATUS</Typography>
                  <Box sx={{ mt: 0.5 }}>{getStatusChip(selectedBatchDetails.status)}</Box>
                </Grid>
              </Grid>

              {/* Stats & Success Ratio */}
              {selectedBatchDetails.status === "Completed" && (
                <Alert severity="success" icon={<CheckCircleOutline />} sx={{ mb: 4, borderRadius: 2 }}>
                  Batch filing succeeded. All valid cases imported, notice documents generated, and notifications dispatched.
                </Alert>
              )}
              {selectedBatchDetails.status === "Processing" && (
                <Box sx={{ mb: 4 }}>
                  <Alert severity="info" sx={{ borderRadius: 2, mb: 1.5 }}>
                    Processing notices and legal notice PDFs. Ingested {selectedBatchDetails.processedCases ?? 0} of {selectedBatchDetails.totalCases} cases. Progress updates automatically.
                  </Alert>
                  <LinearProgress
                    variant="determinate"
                    value={Math.min(100, Math.round(((selectedBatchDetails.processedCases ?? 0) / (selectedBatchDetails.totalCases || 1)) * 100))}
                    sx={{ height: 8, borderRadius: 4 }}
                  />
                </Box>
              )}
              {selectedBatchDetails.status === "Failed" && (
                <Alert severity="error" icon={<ErrorOutline />} sx={{ mb: 4, borderRadius: 2 }}>
                  Ingestion pipeline encountered critical faults during parsing. Please verify CSV encoding.
                </Alert>
              )}

              {/* Batch Cases List */}
              <Typography variant="subtitle1" fontWeight="bold" color="primary.main" mb={2}>
                Ingested Dispute Registry ({selectedBatchDetails.cases?.length || 0} Cases)
              </Typography>
              <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: "300px", borderRadius: 2 }}>
                <Table stickyHeader size="small">
                  <TableHead sx={{ bgcolor: "#f8fafc" }}>
                    <TableRow>
                      <TableCell><strong>Case No / Ref ID</strong></TableCell>
                      <TableCell><strong>Claim Value</strong></TableCell>
                      <TableCell><strong>Respondents</strong></TableCell>
                      <TableCell><strong>Filing Status</strong></TableCell>
                      <TableCell align="right"><strong>Action</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {selectedBatchDetails.cases?.map((c: any, index: number) => (
                      <TableRow key={index} hover>
                        <TableCell>
                          <Typography variant="body2" fontWeight="bold">{c.caseNumber || "Generating..."}</Typography>
                          <Typography variant="caption" color="text.primary" sx={{ fontFamily: "monospace" }}>{c.bulkCaseReferenceNumber}</Typography>
                        </TableCell>
                        <TableCell>₹{c.claimAmount?.toLocaleString()}</TableCell>
                        <TableCell>
                          {c.parties?.filter((p: any) => p.typeofParty === "Respondent").map((p: any) => `${p.firstName} ${p.lastName}`).join(", ") || "N/A"}
                        </TableCell>
                        <TableCell>
                          <Chip label={c.currentStatus} size="small" color={c.currentStatus === "Initiated" ? "secondary" : "primary"} sx={{ fontWeight: "bold" }} />
                        </TableCell>
                        <TableCell align="right">
                          {c.pkCase && (
                            <Button
                              variant="text"
                              size="small"
                              onClick={() => {
                                setDetailsOpen(false);
                                navigate(`/odr-workspace/${c.pkCase}`);
                              }}
                              sx={{ textTransform: "none", fontWeight: "bold" }}
                            >
                              Open Workspace
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                    {(!selectedBatchDetails.cases || selectedBatchDetails.cases.length === 0) && (
                      <TableRow>
                        <TableCell colSpan={5} align="center" sx={{ py: 3, color: "text.secondary" }}>
                          No cases registered for this batch session yet.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </DialogContent>
        <Divider />
        <DialogActions>
          <Button onClick={() => setDetailsOpen(false)} sx={{ textTransform: "none", borderRadius: 2 }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CorporateBatchDashboard;
